/**
 * @file RepContactTabs.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The customer window's tabs on a rep's phone — Details ·
 *              Contracts · Orders · Invoices — and what each shows. Cam,
 *              28 Oct 2026 (rep mobile review, "All 20"): "On a phone the
 *              customer window shows only the account details. Everything in
 *              the right-hand panel on a laptop (contracts and their prices,
 *              orders, invoices, statements) cannot be reached from the phone
 *              at all." Contracts come first after Details, since prices are
 *              what a rep is asked about on a farm.
 *
 * Read only. The laptop's slide-out panel stays as it is; on the phone a tab
 * swaps the window's body (ContactModal + RepMobile.css). Contracts and
 * Invoices reuse the data ContactModal already fetches for its panels; Orders
 * fetches the customer's sales orders (GET /api/orders/customer-orders/search
 * ?accountId=, rep-scoped by the server).
 *
 * The contracts list is deliberately compact — the customer's contracts for
 * the chosen period, with price and how far each has got. The full view is
 * the reps' contracts screen ("My contracts"); `onOpenAllContracts` hands
 * over to it (wired by ContactModal).
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import RepDocCard, { RepDocCards } from './RepDocCard';
import Dropdown from './Dropdown';
import PaginationControls from './PaginationControls';
import { repInvoiceCard, moneyText } from '../utils/repDocCards';
import { deliveryDateParts, repOrderStatus, repPriceState, repOrderItems } from '../utils/repOrderCard';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import useLatestRequest from '../hooks/useLatestRequest';
import './RepOrderCard.css';
import './RepContactTabs.css';

export const REP_CONTACT_TABS = [
  { key: null, label: 'Details' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'rep-orders', label: 'Orders' },
  { key: 'invoices', label: 'Invoices' }
];

/** The tab strip under the customer's name. */
export function RepContactTabBar({ active, onChange, counts = {} }) {
  return (
    <div className="rep-contact-tabs" role="tablist" aria-label="Customer">
      {REP_CONTACT_TABS.map(t => {
        const on = (active || null) === t.key;
        const n = t.key ? counts[t.key] : null;
        return (
          <button
            key={t.label}
            type="button"
            role="tab"
            aria-selected={on}
            data-read-safe
            className={`rep-contact-tab${on ? ' is-active' : ''}`}
            onClick={() => onChange(t.key)}
          >
            {t.label}
            {n > 0 && <span className="rep-contact-tab-count">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

const categoryLabel = (c) => (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Contract');

function contractPeriod(contract) {
  if (contract.is_milk_powder) return contract.mp_range_label || '';
  if (contract.period_name) return contract.period_name;
  if (contract.start_date) return `${formatDateShort(contract.start_date)} – ${formatDateShort(contract.end_date)}`;
  return '';
}

/**
 * The customer's contracts for the chosen period, one compact row each:
 * product, supplier, the price, and the quantity agreed and ordered where a
 * quantity was agreed (most compound contracts have none). Straights are by
 * month, one row per product-month.
 */
export function RepContactContracts({
  contracts = [], loading = false, periods = [], selectedPeriod, onPeriodChange,
  onOpenAllContracts, openAllBlock
}) {
  return (
    <div className="rep-contact-pane">
      {periods.length > 0 && (
        <div className="rep-contact-pane-filter">
          <Dropdown
            value={selectedPeriod}
            onChange={onPeriodChange}
            options={periods.map(p => ({ value: p.name, label: p.name }))}
            placeholder="Choose a period"
          />
        </div>
      )}
      {loading ? (
        <div className="rep-order-cards-empty">Loading contracts…</div>
      ) : contracts.length === 0 ? (
        <div className="rep-order-cards-empty">No contract prices for this period</div>
      ) : (
        <div className="rep-contract-list">
          {contracts.map((c, i) => {
            const agreed = Number(c.quantity) || 0;
            const ordered = Number(c.ordered_quantity) || 0;
            const month = c.category === 'straights' && c.month
              ? new Date(Number(c.year), Number(c.month) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
              : '';
            return (
              <div className="rep-contract-row" key={`${c.id || i}-${c.month || ''}`}>
                <div className="rep-contract-main">
                  <span className="rep-contract-what">
                    {c.product_code && <span className="roc-code">{c.product_code}</span>}
                    <span className="rep-contract-name">{c.product_name || 'Product'}</span>
                  </span>
                  <span className="rep-contract-price">
                    {Number(c.price) > 0 ? formatPrice(c.price) : 'No price'}
                  </span>
                </div>
                <div className="rep-contract-sub">
                  <span>
                    {[categoryLabel(c.category), month || contractPeriod(c),
                      c.supplier_name || c.supplier_company_name || c.mill,
                      c.additive_name ? `+ ${c.additive_name}` : ''].filter(Boolean).join(' · ')}
                  </span>
                  {agreed > 0 && (
                    <span className={ordered > agreed ? 'is-over' : ''}>
                      {formatQuantity(ordered)} of {formatQuantity(agreed)} ordered
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button
        type="button"
        data-read-safe
        className="rep-contact-link"
        onClick={onOpenAllContracts || undefined}
        disabled={!onOpenAllContracts}
        title={onOpenAllContracts ? 'Every contract for this customer, with its orders' : (openAllBlock || undefined)}
      >
        See all contracts, with their orders
        <span className="material-symbols-rounded" aria-hidden="true">chevron_right</span>
      </button>
    </div>
  );
}

/** One customer's sales orders, newest delivery first, each opening the order. */
export function RepContactOrders({ accountId, onOpenOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const latest = useLatestRequest();

  useEffect(() => {
    if (!accountId) return;
    const seq = latest.begin();
    setLoading(true);
    const params = new URLSearchParams({
      accountId: String(accountId), page: String(page), limit: '20',
      sortField: 'delivery_date', sortDirection: 'desc'
    });
    fetch(`${API_URL}/api/orders/customer-orders/search?${params}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(data => {
        if (!latest.isLatest(seq)) return;
        setOrders(data.orders || []);
        setPagination(data.pagination || null);
        setFailed(false);
      })
      .catch(() => { if (latest.isLatest(seq)) { setOrders([]); setFailed(true); } })
      .finally(() => { if (latest.isLatest(seq)) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, page]);

  return (
    <div className="rep-contact-pane">
      <RepDocCards loading={loading} count={orders.length}
        empty={failed ? 'Could not load the orders — close and open the customer again.' : 'No orders for this customer'}>
        {orders.map(order => {
          const items = repOrderItems(order);
          const date = deliveryDateParts(order.delivery_date);
          const status = repOrderStatus(order);
          const price = repPriceState(order);
          const names = items.map(i => i.name);
          return (
            <RepDocCard
              key={order.id}
              number={order.order_number}
              title={names.length ? `${names[0]}${names.length > 1 ? ` + ${names.length - 1} more` : ''}` : 'No lines'}
              when={{ label: date.when || 'Delivery', text: date.text, near: !!date.when }}
              chip={status}
              extraChips={price ? [{ key: 'price', ...price }] : []}
              total={Number(order.total_amount) > 0 ? moneyText(order.total_amount) : ''}
              archived={!!order.is_archived}
              onOpen={() => onOpenOrder && onOpenOrder(order.id)}
              openLabel={`Open order ${order.order_number}`}
            />
          );
        })}
      </RepDocCards>
      {pagination && pagination.totalPages > 1 && (
        <PaginationControls pagination={pagination} onPageChange={setPage} />
      )}
    </div>
  );
}

/** The customer's invoices (ContactModal's own list), each opening the invoice. */
export function RepContactInvoices({ invoices = [], onOpenInvoice, pagination, onPageChange }) {
  return (
    <div className="rep-contact-pane">
      <RepDocCards loading={false} count={invoices.length} empty="No invoices found">
        {invoices.map(inv => {
          const card = repInvoiceCard(inv);
          return (
            <RepDocCard
              key={inv.id}
              number={card.number}
              kind={card.kind === 'Credit note' ? card.kind : ''}
              title={inv.reference ? `Ref ${inv.reference}` : card.kind}
              when={card.when}
              chip={card.chip}
              total={card.total}
              totalSub={card.balance}
              archived={card.archived}
              onOpen={() => onOpenInvoice && onOpenInvoice(inv.id)}
              openLabel={`Open ${card.kind.toLowerCase()} ${card.number}`}
            />
          );
        })}
      </RepDocCards>
      {pagination && pagination.totalPages > 1 && (
        <PaginationControls pagination={pagination} onPageChange={onPageChange} />
      )}
    </div>
  );
}
